import React, { useMemo, useState } from 'react';
import { Chip, CircularProgress, IconButton, Paper, Table, Typography } from '@esgian/esgianui';
import Tooltip from '@mui/material/Tooltip';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { useSelector } from 'react-redux';
import { getSessionStatus } from '@store/features';
import { APP_NAME } from '@constants/index';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@hooks';
import { useTimezone } from '@hooks/useTimezone';
import { getAllExecutions, getMyReportRequests, getArchivedReports, downloadExecutionReport } from '@api/ReportScheduler';
import moment from 'moment-timezone';
import { DATE_TIME_FORMAT } from '@constants/dates';

function ExecutionsTab() {
  const sessionStatus = useSelector(getSessionStatus);
  const [downloadingIds, setDownloadingIds] = useState(new Set());
  const { selectedTimeZone } = useTimezone();

  const {
    theme: {
      palette: { mode }
    }
  } = useTheme();

  const requestsQuery = useQuery({
    queryKey: ['scheduledReport', 'myRequests'],
    enabled: sessionStatus === 'ok',
    placeholderData: [],
    queryFn: ({ signal }) => getMyReportRequests(signal)
  });

  const archivedQuery = useQuery({
    queryKey: ['scheduledReport', 'archived'],
    enabled: sessionStatus === 'ok',
    placeholderData: [],
    queryFn: ({ signal }) => getArchivedReports(signal)
  });

  const executionsQuery = useQuery({
    queryKey: ['scheduledReport', 'executions'],
    enabled: sessionStatus === 'ok',
    placeholderData: [],
    gcTime: 0,
    staleTime: 0,
    queryFn: ({ signal }) => getAllExecutions(signal)
  });

  const requestNameMap = useMemo(() => {
    const map = {};
    (requestsQuery.data || []).forEach((r) => {
      map[r.id] = r.reportName;
    });
    (archivedQuery.data || []).forEach((r) => {
      map[r.id] = r.reportName;
    });
    return map;
  }, [requestsQuery.data, archivedQuery.data]);

  const executions = useMemo(
    () =>
      (executionsQuery.data || []).map((ex) => ({
        ...ex,
        reportName: requestNameMap[ex.reportRequestId] || '--'
      })),
    [executionsQuery.data, requestNameMap]
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    return selectedTimeZone
      ? moment.tz(dateStr, selectedTimeZone).format(DATE_TIME_FORMAT)
      : moment(dateStr).format(DATE_TIME_FORMAT);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'delivered' || s === 'generated') return 'success';
    if (s === 'failed') return 'error';
    if (s === 'generating' || s === 'inprogress') return 'warning';
    return 'default';
  };

  const handleDownload = async (execution) => {
    setDownloadingIds((prev) => new Set(prev).add(execution.id));
    try {
      const controller = new AbortController();
      await downloadExecutionReport(controller.signal, execution.id, execution.generatedReportId);
    } finally {
      setDownloadingIds((prev) => {
        const next = new Set(prev);
        next.delete(execution.id);
        return next;
      });
    }
  };

  const columns = useMemo(
    () => [
      {
        name: 'reportName',
        label: 'Report',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => value || '--'
        }
      },
      {
        name: 'executionDate',
        label: 'Execution Date',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => formatDate(value)
        }
      },
      {
        name: 'status',
        label: 'Status',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => (
            <Chip
              label={value || '--'}
              size="small"
              color={getStatusColor(value)}
              variant="outlined"
            />
          )
        }
      },
      {
        name: 'periodStart',
        label: 'Period Start',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => formatDate(value)
        }
      },
      {
        name: 'periodEnd',
        label: 'Period End',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => formatDate(value)
        }
      },
      {
        name: 'deliveredToEmails',
        label: 'Delivered To',
        options: {
          sort: true,
          sortThirdClickReset: true,
          customBodyRender: (value) => {
            if (!Array.isArray(value) || value.length === 0) return '--';
            return <Typography variant="body2">{value.join(', ')}</Typography>;
          },
          sortCompare: (order) => (a, b) => {
            const aVal = Array.isArray(a.data) ? a.data.join(', ') : '';
            const bVal = Array.isArray(b.data) ? b.data.join(', ') : '';
            return aVal.localeCompare(bVal) * (order === 'asc' ? 1 : -1);
          }
        }
      },
      {
        name: 'generatedReportId',
        label: ' ',
        options: {
          sort: false,
          filter: false,
          searchable: false,
          customBodyRender: (value, tableMeta) => {
            if (!value) return null;
            const execution = executions.find((ex) => ex.generatedReportId === value);
            if (!execution) return null;
            const isDownloading = downloadingIds.has(execution.id);
            return (
              <Tooltip title="Download report">
                <IconButton
                  size="small"
                  disabled={isDownloading}
                  onClick={() => handleDownload(execution)}>
                  {isDownloading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            );
          }
        }
      }
    ],
    [executions, downloadingIds]
  );

  const options = useMemo(
    () => ({
      selectableRows: 'none',
      responsive: 'standard',
      search: true,
      download: false,
      print: false,
      viewColumns: false,
      filter: false,
      pagination: true,
      elevation: 0,
      toolbar: true,
      rowsPerPageOptions: [10, 25, 50, 100],
      sortOrder: {
        name: 'executionDate',
        direction: 'desc'
      }
    }),
    []
  );

  return (
    <Paper sx={{ overflow: 'visible' }}>
      <Table
        customStyle={{}}
        title="Executions"
        mode={mode}
        data={executions}
        columns={columns}
        options={options}
        app={APP_NAME}
        loading={executionsQuery.isFetching}
      />
    </Paper>
  );
}

export default ExecutionsTab;
